import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Receipt,
    FileText,
    CreditCard,
    ArrowLeft,
    ArrowUpRight,
    Plus,
    TrendingUp,
    FileSpreadsheet,
    Download,
    Eye,
    Trash2,
    X,
    Wand2,
    Loader2,
    ChevronDown,
    Printer,
    Calendar,
    Clock,
    Building2,
    Users,
    MessageSquare,
    ThumbsUp,
    Globe
} from "lucide-react";

import { GROWTHOS_BASE } from "../config/api";

// Same backend everything else in the dashboard already talks to
// (see Mediasection.js) — narration/comment generation below tries
// this endpoint first and falls back to a local template if it's
// not wired up yet on the API side. Base URL comes from the shared config now.
const GROWTHOS_API_BASE = GROWTHOS_BASE;
const INVOICE_NARRATION_ENDPOINT = `${GROWTHOS_API_BASE}/api/generate-invoice-narration`;
const INVOICE_COMMENT_ENDPOINT = `${GROWTHOS_API_BASE}/api/generate-invoice-comment`;
const INVOICES_ENDPOINT = `${GROWTHOS_API_BASE}/invoices`;
const BILL_NARRATION_ENDPOINT = `${GROWTHOS_API_BASE}/api/generate-bill-narration`;
const BILL_COMMENT_ENDPOINT = `${GROWTHOS_API_BASE}/api/generate-bill-comment`;
const BILLS_ENDPOINT = `${GROWTHOS_API_BASE}/bills`;
const AUTH_TOKEN_KEY = "growthos_token";
const authHeaders = () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
};

/* ==========================================================
   Backend stores invoices with snake_case keys (invoice_number,
   seller_address, ...) — the form/table/print-view in this file
   were built around camelCase (invoiceNumber, sellerAddress, ...).
   These two converters keep every existing component untouched
   and only translate at the API boundary.
========================================================== */
const toApiInvoice = (form) => ({
    invoice_number: form.invoiceNumber || "",
    invoice_date: form.invoiceDate || "",
    seller_address: form.sellerAddress || "",
    buyer_address: form.buyerAddress || "",
    tax_percent: Number(form.taxPercent) || 0,
    invoice_amount: Number(form.invoiceAmount) || 0,
    total_amount: Number(form.totalAmount) || 0,
    paid_amount: Number(form.paidAmount) || 0,
    amount_type: form.amountType || "",
    payment_type: form.paymentType || "",
    narration: form.narration || "",
    comments: form.comments || ""
});

const fromApiInvoice = (row) => ({
    id: row.id,
    invoiceNumber: row.invoice_number || "",
    invoiceDate: row.invoice_date || "",
    sellerAddress: row.seller_address || "",
    buyerAddress: row.buyer_address || "",
    taxPercent: row.tax_percent ?? "",
    invoiceAmount: row.invoice_amount ?? "",
    totalAmount: row.total_amount ?? "",
    paidAmount: row.paid_amount ?? "",
    amountType: row.amount_type || "",
    paymentType: row.payment_type || "",
    narration: row.narration || "",
    comments: row.comments || ""
});

/* ==========================================================
   Backend stores bills with snake_case keys (vendor_name,
   bill_amount, ...) — same translate-at-the-boundary pattern
   as toApiInvoice/fromApiInvoice above.
========================================================== */
const toApiBill = (form) => ({
    vendor_name: form.vendorName || "",
    bill_date: form.billDate || "",
    expense_head: form.expenseHead || "",
    bill_amount: Number(form.billAmount) || 0,
    paid_amount: Number(form.paidAmount) || 0,
    amount_type: form.amountType || "",
    payment_type: form.paymentType || "",
    narration: form.narration || "",
    comments: form.comments || ""
});

const fromApiBill = (row) => ({
    id: row.id,
    vendorName: row.vendor_name || "",
    billDate: row.bill_date || "",
    expenseHead: row.expense_head || "",
    billAmount: row.bill_amount ?? "",
    paidAmount: row.paid_amount ?? "",
    amountType: row.amount_type || "",
    paymentType: row.payment_type || "",
    narration: row.narration || "",
    comments: row.comments || ""
});

/* ==========================================================
   Shared field tokens — lifted straight from Mediasection.js
   so every input/label in this module matches the rest of the
   dashboard exactly (same class strings, same spacing rhythm).
========================================================== */
const Field = ({ label, required, children }) => (
    <div>
        <label className="text-xs font-semibold text-gray-400 tracking-wide uppercase">
            {label} {required && <span className="text-orange-500">*</span>}
        </label>
        <div className="mt-2">{children}</div>
    </div>
);
const inputClass =
    "w-full bg-white/[0.04] border border-white/10 text-white text-sm rounded-xl px-4 py-2.5 outline-none transition focus:border-orange-500/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-orange-500/10 placeholder:text-gray-600";

/* ==========================================================
   AccountingCard — MediaSection's PremiumModuleCard, reused
   as-is for the Invoices / Bills landing tiles.
========================================================== */
const AccountingCard = ({ icon: Icon, eyebrow, title, description, status = "Ready", onOpen }) => (
    <div
        onClick={onOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onOpen();
        }}
        className="group relative cursor-pointer overflow-hidden rounded-[20px] border border-white/[0.1] bg-gradient-to-br from-orange-500/[0.07] via-white/[0.035] to-transparent p-6 backdrop-blur-2xl transition-all duration-300 hover:-translate-y-1 hover:border-orange-400/45 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_24px_48px_-24px_rgba(0,0,0,0.65),0_12px_28px_-14px_rgba(255,107,0,0.22)] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_32px_64px_-20px_rgba(255,107,0,0.42)]"
    >
        <div className="pointer-events-none absolute -top-16 -right-10 h-40 w-40 rounded-full bg-orange-500/25 blur-[70px] opacity-60 transition-opacity duration-500 group-hover:opacity-100" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-32 w-32 rounded-full bg-orange-500/20 blur-[60px] opacity-35 transition-opacity duration-500 group-hover:opacity-100" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/45 to-transparent" />
        <div className="pointer-events-none absolute right-5 top-5 h-3 w-3 border-r border-t border-orange-300/30 transition-colors duration-300 group-hover:border-orange-300/60" />

        <div className="relative flex items-center justify-between">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-orange-400/25 bg-orange-500/[0.12] shadow-[0_0_24px_-6px_rgba(255,107,0,0.45)] backdrop-blur-md transition-all duration-300 group-hover:border-orange-400/50 group-hover:bg-orange-500/[0.22] group-hover:shadow-[0_0_32px_-4px_rgba(255,107,0,0.6)]">
                <Icon size={19} className="text-orange-300 transition-colors duration-300 group-hover:text-orange-200" />
            </div>
            <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-orange-200/50">{eyebrow}</span>
        </div>

        <h3 className="relative mt-5 text-[18px] font-semibold tracking-tight text-white">{title}</h3>
        <p className="relative mt-2 text-[13.5px] leading-relaxed text-zinc-400">{description}</p>

        <div className="relative mt-6 flex items-center justify-between border-t border-white/[0.08] pt-4">
            <span className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500/80" />
                {status}
            </span>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onOpen();
                }}
                className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-[12px] font-medium text-zinc-200 transition-all duration-300 group-hover:border-orange-400/40 group-hover:bg-orange-500/[0.14] group-hover:text-orange-300"
            >
                Open
                <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
        </div>
    </div>
);

/* ==========================================================
   Toolbar button — one primary (Add Invoice) + three secondary
   glass buttons (Revenue Trends / Export to Tally / QuickBooks).
========================================================== */
const ToolbarButton = ({ icon: Icon, label, onClick, primary, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={
            (primary
                ? "inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white text-sm font-semibold px-4 py-2.5 shadow-[0_8px_24px_-8px_rgba(255,107,0,0.55)] transition-all duration-200"
                : "inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] hover:border-orange-400/40 text-gray-200 hover:text-orange-300 text-sm font-medium px-4 py-2.5 transition-all duration-200") +
            (disabled ? " opacity-50 cursor-not-allowed" : "")
        }
    >
        <Icon size={15} />
        {label}
    </button>
);

/* ==========================================================
   A <select> backed by the shared custom_dropdown_options table
   (GET/POST /dropdown-options/{field}) — same "type once, becomes
   a real option for everyone after that" behavior already used on
   the Company Registration / Target Audience forms. Picking
   "Other…" swaps in a text input; committing it (Enter or blur)
   saves it to the DB and switches back to the dropdown with the
   new value selected.
========================================================== */
const OTHER_VALUE = "__other__";

const DropdownWithOther = ({ field, value, onChange, placeholder, defaultOptions = [] }) => {
    const [options, setOptions] = useState(defaultOptions);
    const [open, setOpen] = useState(false);
    const [showOther, setShowOther] = useState(false);
    const [otherValue, setOtherValue] = useState("");
    const [saving, setSaving] = useState(false);
    const wrapRef = useRef(null);

    // Pull in whatever's already been saved in the DB for this field
    // (merged with the built-in defaults, deduped case-insensitively)
    // — falls back to just the defaults if the server isn't reachable.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${GROWTHOS_API_BASE}/dropdown-options/${field}`, { headers: authHeaders() });
                if (!res.ok) throw new Error("failed to load options");
                const data = await res.json();
                const fetched = data?.data || [];
                if (cancelled || !fetched.length) return;
                setOptions((prev) => {
                    const seen = new Set(prev.map((o) => o.toLowerCase()));
                    const merged = [...prev];
                    fetched.forEach((v) => {
                        if (!seen.has(v.toLowerCase())) {
                            merged.push(v);
                            seen.add(v.toLowerCase());
                        }
                    });
                    return merged;
                });
            } catch (err) {
                // stay on defaultOptions
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [field]);

    // If the current value (e.g. loaded from an existing invoice) isn't
    // in the list yet, add it so the trigger doesn't silently show blank.
    useEffect(() => {
        if (value && !options.some((o) => o.toLowerCase() === value.toLowerCase())) {
            setOptions((prev) => [...prev, value]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Close the popover on outside click, same pattern any custom
    // dropdown needs since there's no native <select> here anymore.
    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [open]);

    const selectValue = (v) => {
        if (v === OTHER_VALUE) {
            setOtherValue("");
            setShowOther(true);
            setOpen(false);
        } else {
            onChange(v);
            setOpen(false);
        }
    };

    const commitOther = async () => {
        const trimmed = otherValue.trim();
        if (!trimmed) {
            setShowOther(false);
            return;
        }
        onChange(trimmed);
        setShowOther(false);
        setSaving(true);
        try {
            const res = await fetch(`${GROWTHOS_API_BASE}/dropdown-options/${field}`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ value: trimmed })
            });
            if (res.ok) {
                const data = await res.json();
                if (data?.data?.length) setOptions(data.data);
            }
            // If the save fails, the typed value still sits in the form
            // (onChange already ran) — it just won't be offered as a
            // dropdown option next time until it's saved successfully.
        } catch (err) {
            // same as above — form keeps the typed value regardless
        } finally {
            setSaving(false);
        }
    };

    if (showOther) {
        return (
            <input
                type="text"
                autoFocus
                className={inputClass}
                placeholder="Type a new value and press Enter"
                value={otherValue}
                disabled={saving}
                onChange={(e) => setOtherValue(e.target.value)}
                onBlur={commitOther}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        commitOther();
                    } else if (e.key === "Escape") {
                        setShowOther(false);
                    }
                }}
            />
        );
    }

    return (
        <div ref={wrapRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={`${inputClass} flex items-center justify-between text-left cursor-pointer ${value ? "" : "text-gray-600"}`}
            >
                <span className="truncate">{value || placeholder}</span>
                <ChevronDown size={15} className={`text-gray-500 shrink-0 ml-2 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div className="absolute z-20 left-0 right-0 mt-1.5 max-h-56 overflow-y-auto thin-scrollbar rounded-xl border border-white/10 bg-[#14141a] shadow-[0_24px_60px_-16px_rgba(0,0,0,0.75)] p-1.5">
                    {options.map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            onClick={() => selectValue(opt)}
                            className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-colors ${
                                opt === value ? "bg-orange-500/15 text-orange-300 font-medium" : "text-gray-200 hover:bg-white/[0.06]"
                            }`}
                        >
                            {opt}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={() => selectValue(OTHER_VALUE)}
                        className="w-full text-left text-sm px-3 py-2 mt-1 pt-2 rounded-lg text-orange-300 hover:bg-orange-500/10 transition-colors border-t border-white/5"
                    >
                        + Other…
                    </button>
                </div>
            )}
        </div>
    );
};

const emptyInvoiceForm = {
    invoiceNumber: "",
    invoiceDate: "",
    sellerAddress: "",
    buyerAddress: "",
    taxPercent: "",
    invoiceAmount: "",
    totalAmount: "",
    paidAmount: "",
    amountType: "",
    paymentType: "",
    narration: "",
    comments: ""
};

const SEED_INVOICES = [
    {
        id: 1,
        invoiceNumber: "IN5693",
        invoiceDate: "2023-02-02",
        sellerAddress: "7th Floor, 705 Raikar Chamber, Govandi East, Military Rd, Maharashtra, Mumbai, 400088",
        buyerAddress: "6th Floor, 605 Raikar Chamber, Govandi East, Military Rd, Maharashtra, Mumbai, 400088",
        invoiceAmount: "5000.00",
        totalAmount: "5900.00",
        paymentType: "Online",
        narration: "High Net Value",
        comments: "Invoice for High Net Value"
    }
];

const emptyBillForm = {
    vendorName: "",
    billDate: "",
    expenseHead: "",
    billAmount: "",
    paidAmount: "",
    amountType: "",
    paymentType: "",
    narration: "",
    comments: ""
};

const SEED_BILLS = [
    {
        id: 1,
        vendorName: "Anisha Jai",
        billDate: "2023-02-02",
        expenseHead: "Bank",
        billAmount: "12000.00",
        paidAmount: "6000.00",
        amountType: "INR",
        paymentType: "Cash",
        narration: "High Net Value 1",
        comments: "Bills"
    }
];

/* ==========================================================
   Add / Edit Invoice modal
========================================================== */
const AddInvoiceModal = ({ onClose, onSave }) => {
    const [form, setForm] = useState(emptyInvoiceForm);
    const [narrationLoading, setNarrationLoading] = useState(false);
    const [commentLoading, setCommentLoading] = useState(false);

    const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const buildFallback = (kind) => {
        const who = form.buyerAddress ? form.buyerAddress.split(",")[0] : "the buyer";
        const amount = form.invoiceAmount || form.totalAmount || "0";
        if (kind === "narration") {
            return `Payment of INR ${amount} for invoice ${form.invoiceNumber || "—"} raised on ${form.invoiceDate || "—"} to ${who}.`;
        }
        return `Reviewed and approved invoice ${form.invoiceNumber || "—"} for INR ${amount}, payment via ${form.paymentType || "the selected method"}.`;
    };

    const generate = async (kind) => {
        const endpoint = kind === "narration" ? INVOICE_NARRATION_ENDPOINT : INVOICE_COMMENT_ENDPOINT;
        const setLoading = kind === "narration" ? setNarrationLoading : setCommentLoading;
        setLoading(true);
        try {
            const res = await fetch(endpoint, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify(form)
            });
            if (!res.ok) throw new Error("generation failed");
            const data = await res.json();
            const text = data?.text || data?.narration || data?.comment || buildFallback(kind);
            setForm((f) => ({ ...f, [kind]: text }));
        } catch (err) {
            // Backend endpoint isn't wired up yet — fall back to a
            // sensible local draft so the button still does something.
            setForm((f) => ({ ...f, [kind]: buildFallback(kind) }));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(form);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto thin-scrollbar rounded-2xl border border-white/10 bg-[#0b0b0f] shadow-[0_32px_80px_-20px_rgba(0,0,0,0.8)]">
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#0b0b0f]/95 backdrop-blur px-6 py-4">
                    <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                            <FileText size={15} className="text-orange-500" />
                        </div>
                        <h3 className="text-white text-sm font-semibold">Add New Invoice</h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Invoice Number" required>
                            <input
                                className={inputClass}
                                placeholder="Enter invoice number"
                                value={form.invoiceNumber}
                                onChange={update("invoiceNumber")}
                                required
                            />
                        </Field>
                        <Field label="Invoice Date" required>
                            <input
                                type="date"
                                className={`${inputClass} [color-scheme:dark]`}
                                value={form.invoiceDate}
                                onChange={update("invoiceDate")}
                                required
                            />
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Seller Address Details">
                            <textarea
                                rows={2}
                                className={`${inputClass} resize-none`}
                                placeholder="Enter seller address"
                                value={form.sellerAddress}
                                onChange={update("sellerAddress")}
                            />
                        </Field>
                        <Field label="Buyer Address Details">
                            <textarea
                                rows={2}
                                className={`${inputClass} resize-none`}
                                placeholder="Enter buyer address"
                                value={form.buyerAddress}
                                onChange={update("buyerAddress")}
                            />
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Tax Percent (%)">
                            <input
                                type="number"
                                className={inputClass}
                                placeholder="Enter tax percent"
                                value={form.taxPercent}
                                onChange={update("taxPercent")}
                            />
                        </Field>
                        <Field label="Invoice Amount" required>
                            <input
                                type="number"
                                className={inputClass}
                                placeholder="Enter invoice amount"
                                value={form.invoiceAmount}
                                onChange={update("invoiceAmount")}
                                required
                            />
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Total Amount" required>
                            <input
                                type="number"
                                className={inputClass}
                                placeholder="Enter total amount"
                                value={form.totalAmount}
                                onChange={update("totalAmount")}
                                required
                            />
                        </Field>
                        <Field label="Paid Amount">
                            <input
                                type="number"
                                className={inputClass}
                                placeholder="Enter paid amount"
                                value={form.paidAmount}
                                onChange={update("paidAmount")}
                            />
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Amount Type">
                            <DropdownWithOther
                                field="currency"
                                value={form.amountType}
                                onChange={(v) => setForm((f) => ({ ...f, amountType: v }))}
                                placeholder="Select amount type"
                                defaultOptions={["INR", "USD", "EUR", "GBP"]}
                            />
                        </Field>
                        <Field label="Payment Type">
                            <DropdownWithOther
                                field="payment_type"
                                value={form.paymentType}
                                onChange={(v) => setForm((f) => ({ ...f, paymentType: v }))}
                                placeholder="Select Payment Type"
                                defaultOptions={["Online", "Cash", "Demand Draft"]}
                            />
                        </Field>
                    </div>

                    <Field label="Narration">
                        <textarea
                            rows={2}
                            className={`${inputClass} resize-none`}
                            placeholder="Enter narration"
                            value={form.narration}
                            onChange={update("narration")}
                        />
                    </Field>
                    <button
                        type="button"
                        onClick={() => generate("narration")}
                        disabled={narrationLoading}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-orange-500/30 bg-gradient-to-r from-orange-500/20 to-orange-600/10 hover:bg-orange-500/20 text-orange-300 text-sm font-medium py-2.5 transition disabled:opacity-60"
                    >
                        {narrationLoading ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
                        Generate Narration
                    </button>

                    <Field label="Comments">
                        <textarea
                            rows={2}
                            className={`${inputClass} resize-none`}
                            placeholder="Enter comments"
                            value={form.comments}
                            onChange={update("comments")}
                        />
                    </Field>
                    <button
                        type="button"
                        onClick={() => generate("comments")}
                        disabled={commentLoading}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-orange-500/30 bg-gradient-to-r from-orange-500/20 to-orange-600/10 hover:bg-orange-500/20 text-orange-300 text-sm font-medium py-2.5 transition disabled:opacity-60"
                    >
                        {commentLoading ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
                        Generate Comment
                    </button>

                    <button
                        type="submit"
                        className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold text-sm py-3 shadow-[0_8px_24px_-8px_rgba(255,107,0,0.55)] transition"
                    >
                        Add Invoice
                    </button>
                </form>
            </div>
        </div>
    );
};

/* ==========================================================
   Add / Edit Bill modal — same shape/behavior as AddInvoiceModal,
   fields swapped for the vendor-bill side of Company Accounting.
========================================================== */
const AddBillModal = ({ onClose, onSave }) => {
    const [form, setForm] = useState(emptyBillForm);
    const [narrationLoading, setNarrationLoading] = useState(false);
    const [commentLoading, setCommentLoading] = useState(false);

    const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const buildFallback = (kind) => {
        const amount = form.billAmount || form.paidAmount || "0";
        if (kind === "narration") {
            return `Payment of INR ${amount} to ${form.vendorName || "the vendor"} for ${form.expenseHead || "expenses"} on ${form.billDate || "—"}.`;
        }
        return `Reviewed and approved bill from ${form.vendorName || "the vendor"} for INR ${amount}, payment via ${form.paymentType || "the selected method"}.`;
    };

    const generate = async (kind) => {
        const endpoint = kind === "narration" ? BILL_NARRATION_ENDPOINT : BILL_COMMENT_ENDPOINT;
        const setLoading = kind === "narration" ? setNarrationLoading : setCommentLoading;
        setLoading(true);
        try {
            const res = await fetch(endpoint, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify(form)
            });
            if (!res.ok) throw new Error("generation failed");
            const data = await res.json();
            const text = data?.text || data?.narration || data?.comment || buildFallback(kind);
            setForm((f) => ({ ...f, [kind]: text }));
        } catch (err) {
            // Backend endpoint isn't wired up yet — fall back to a
            // sensible local draft so the button still does something.
            setForm((f) => ({ ...f, [kind]: buildFallback(kind) }));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(form);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto thin-scrollbar rounded-2xl border border-white/10 bg-[#0b0b0f] shadow-[0_32px_80px_-20px_rgba(0,0,0,0.8)]">
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#0b0b0f]/95 backdrop-blur px-6 py-4">
                    <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                            <CreditCard size={15} className="text-orange-500" />
                        </div>
                        <h3 className="text-white text-sm font-semibold">Add New Bill</h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Vendor Name" required>
                            <input
                                className={inputClass}
                                placeholder="Enter vendor name"
                                value={form.vendorName}
                                onChange={update("vendorName")}
                                required
                            />
                        </Field>
                        <Field label="Bill Amount" required>
                            <input
                                type="number"
                                className={inputClass}
                                placeholder="Enter bill amount"
                                value={form.billAmount}
                                onChange={update("billAmount")}
                                required
                            />
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Bill Date" required>
                            <input
                                type="date"
                                className={`${inputClass} [color-scheme:dark]`}
                                value={form.billDate}
                                onChange={update("billDate")}
                                required
                            />
                        </Field>
                        <Field label="Amount Type">
                            <DropdownWithOther
                                field="currency"
                                value={form.amountType}
                                onChange={(v) => setForm((f) => ({ ...f, amountType: v }))}
                                placeholder="Select Amount Type"
                                defaultOptions={["INR", "USD", "EUR", "GBP"]}
                            />
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Paid Amount">
                            <input
                                type="number"
                                className={inputClass}
                                placeholder="Enter paid amount"
                                value={form.paidAmount}
                                onChange={update("paidAmount")}
                            />
                        </Field>
                        <Field label="Payment Type">
                            <DropdownWithOther
                                field="payment_type"
                                value={form.paymentType}
                                onChange={(v) => setForm((f) => ({ ...f, paymentType: v }))}
                                placeholder="Select Payment Type"
                                defaultOptions={["Online", "Cash", "Demand Draft"]}
                            />
                        </Field>
                    </div>

                    <Field label="Expense Head">
                        <DropdownWithOther
                            field="expense_head"
                            value={form.expenseHead}
                            onChange={(v) => setForm((f) => ({ ...f, expenseHead: v }))}
                            placeholder="Select Expense Head"
                            defaultOptions={["Bank", "Accounting", "Telephone", "Online Shopping", "Restaurant", "Meals & Entertainment", "Job Supplies"]}
                        />
                    </Field>

                    <Field label="Narration">
                        <textarea
                            rows={2}
                            className={`${inputClass} resize-none`}
                            placeholder="Enter narration"
                            value={form.narration}
                            onChange={update("narration")}
                        />
                    </Field>
                    <button
                        type="button"
                        onClick={() => generate("narration")}
                        disabled={narrationLoading}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-orange-500/30 bg-gradient-to-r from-orange-500/20 to-orange-600/10 hover:bg-orange-500/20 text-orange-300 text-sm font-medium py-2.5 transition disabled:opacity-60"
                    >
                        {narrationLoading ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
                        Generate Narration
                    </button>

                    <Field label="Comments">
                        <textarea
                            rows={2}
                            className={`${inputClass} resize-none`}
                            placeholder="Enter comments"
                            value={form.comments}
                            onChange={update("comments")}
                        />
                    </Field>
                    <button
                        type="button"
                        onClick={() => generate("comments")}
                        disabled={commentLoading}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-orange-500/30 bg-gradient-to-r from-orange-500/20 to-orange-600/10 hover:bg-orange-500/20 text-orange-300 text-sm font-medium py-2.5 transition disabled:opacity-60"
                    >
                        {commentLoading ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
                        Generate Comment
                    </button>

                    <button
                        type="submit"
                        className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold text-sm py-3 shadow-[0_8px_24px_-8px_rgba(255,107,0,0.55)] transition"
                    >
                        Add Bill
                    </button>
                </form>
            </div>
        </div>
    );
};

/* ==========================================================
   "View" modal — renders the invoice as an actual white,
   print-ready document (the way it'll look on paper / PDF),
   inside a dark chrome frame that carries Print/Close controls.
   #invoice-print-area is the only thing that survives @media
   print (see the print rules in the <style> block below), so
   hitting Print produces a clean one-page invoice, not the
   whole dashboard.
========================================================== */
const readStoredCompanyName = () => {
    try {
        const raw = localStorage.getItem("growthos_company");
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed?.company_name || "Your Company";
    } catch (err) {
        return "Your Company";
    }
};

// The company's own registered website (company_registrations.website —
// same object /auth/login and /auth/me already return, just unused here
// until now) — this is what should show at the bottom of the invoice,
// NOT this app's own localhost/deployed URL.
const readStoredCompanyWebsite = () => {
    try {
        const raw = localStorage.getItem("growthos_company");
        const parsed = raw ? JSON.parse(raw) : null;
        return (parsed?.website || "").trim();
    } catch (err) {
        return "";
    }
};

// Platform/product name shown centered in the invoice header — same
// brand across every company's invoice (like a "Powered by" mark),
// separate from the per-company name pulled from login.
const PLATFORM_NAME = "ISF Athena";

// Small helper: turn "Orbitrix Technologies Pvt Ltd" -> "OT" for the
// little logo badge in the top-right when the company hasn't uploaded
// an actual logo image.
const companyInitials = (name) =>
    (name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase() || "CO";

const ViewInvoiceModal = ({ invoice, onClose }) => {
    const companyName = useMemo(readStoredCompanyName, []);
    const now = useMemo(() => new Date(), []);
    const dateLine = useMemo(
        () => now.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
        [now]
    );
    const timeLine = useMemo(
        () =>
            `${now.toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false
            })}`,
        [now]
    );
    const companyWebsite = useMemo(readStoredCompanyWebsite, []);
    const initials = useMemo(() => companyInitials(companyName), [companyName]);
    const rate = Number(invoice.invoiceAmount) || 0;
    const total = Number(invoice.totalAmount) || 0;
    const paid = Number(invoice.paidAmount) || 0;
    const balanceDue = Math.max(total - paid, 0);
    // The company's own website (from their registration) — falls back to
    // just their name if they never filled that field in, but NEVER to
    // this app's own host, which is what used to leak here (localhost in
    // dev, or wherever this dashboard itself happens to be deployed).
    const footerUrl = companyWebsite || companyName;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-4 sm:p-8 print:bg-white print:p-0 print:block">
            {/* ---- chrome: hidden entirely when printing ---- */}
            <div className="w-full max-w-2xl print:hidden mb-4 flex items-center justify-between sticky top-0">
                <span className="inline-flex items-center gap-2 text-xs font-medium text-gray-300 bg-black/40 border border-white/10 rounded-full px-3 py-1.5">
                    <Eye size={12} className="text-orange-400" /> Invoice preview
                </span>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => window.print()}
                        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white text-xs font-semibold px-3.5 py-1.5 shadow-[0_6px_18px_-6px_rgba(255,107,0,0.55)] transition"
                    >
                        <Printer size={13} /> Print / Save as PDF
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-7 w-7 rounded-full flex items-center justify-center text-gray-300 bg-black/40 border border-white/10 hover:text-white hover:bg-white/10 transition"
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>

            {/* ---- the actual white, printable invoice ---- */}
            <div
                id="invoice-print-area"
                className="w-full max-w-2xl bg-white text-gray-900 rounded-2xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none print:max-w-none"
            >
                {/* top orange -> navy accent strip */}
                <div className="h-2 w-full bg-gradient-to-r from-orange-500 via-orange-500 to-[#0b1b3d]" />

                <div className="p-8 sm:p-10">
                    {/* header: timestamp | platform brand | company logo+name */}
                    <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1.5 text-[11px] text-gray-500">
                            <div className="flex items-center gap-1.5">
                                <Calendar size={13} className="text-gray-400" /> {dateLine}
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Clock size={13} className="text-gray-400" /> {timeLine}
                            </div>
                        </div>

                        <div className="hidden sm:flex flex-col items-center text-center">
                            <Building2 size={22} className="text-orange-500" />
                            <span className="mt-1 text-sm font-bold text-[#0b1b3d]">{PLATFORM_NAME}</span>
                        </div>

                        <div className="flex flex-col items-end text-right">
                            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-orange-500 to-[#0b1b3d] flex items-center justify-center text-white text-xs font-bold shrink-0">
                                {initials}
                            </div>
                            <span className="mt-1.5 text-sm font-semibold text-orange-600">{companyName}</span>
                            <span className="mt-1 h-px w-16 bg-orange-500/40" />
                        </div>
                    </div>

                    <h1 className="mt-8 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0b1b3d]">INVOICE</h1>
                    <div className="mt-3 text-sm text-gray-600 space-y-0.5">
                        <p>
                            Invoice #: <span className="text-orange-600 font-bold">{invoice.invoiceNumber || "N/A"}</span>
                        </p>
                        <p>
                            Date: <span className="text-gray-900 font-medium">{invoice.invoiceDate || "N/A"}</span>
                        </p>
                    </div>

                    {/* billed from / billed to */}
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="border border-gray-200 rounded-xl p-5">
                            <div className="flex items-center gap-2.5">
                                <span className="h-7 w-7 shrink-0 rounded-full bg-orange-500 flex items-center justify-center text-white">
                                    <FileText size={13} />
                                </span>
                                <h2 className="text-xs font-bold text-[#0b1b3d] tracking-wide">BILLED FROM</h2>
                            </div>
                            <p className="mt-3 text-sm font-semibold text-orange-600">{companyName}</p>
                            <p className="mt-1 text-xs text-gray-500 whitespace-pre-line break-words leading-relaxed">
                                {invoice.sellerAddress || "N/A"}
                            </p>
                        </div>
                        <div className="border border-gray-200 rounded-xl p-5">
                            <div className="flex items-center gap-2.5">
                                <span className="h-7 w-7 shrink-0 rounded-full bg-orange-500 flex items-center justify-center text-white">
                                    <Users size={13} />
                                </span>
                                <h2 className="text-xs font-bold text-[#0b1b3d] tracking-wide">BILLED TO</h2>
                            </div>
                            <p className="mt-3 text-xs text-gray-500 whitespace-pre-line break-words leading-relaxed">
                                {invoice.buyerAddress || "N/A"}
                            </p>
                        </div>
                    </div>

                    {/* line items */}
                    <div className="mt-6 overflow-hidden rounded-xl">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-[#0b1b3d] text-[10px] font-bold text-white uppercase tracking-wider">
                                    <th className="text-left px-4 py-3">ID</th>
                                    <th className="text-left px-4 py-3">Description</th>
                                    <th className="text-right px-4 py-3">Qty</th>
                                    <th className="text-right px-4 py-3">Rate</th>
                                    <th className="text-right px-4 py-3">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b border-gray-100">
                                    <td className="px-4 py-3 text-gray-700">1</td>
                                    <td className="px-4 py-3 text-gray-700">{invoice.narration || "Invoice Service"}</td>
                                    <td className="px-4 py-3 text-right text-gray-700">1</td>
                                    <td className="px-4 py-3 text-right text-gray-700">INR {rate.toFixed(2)}</td>
                                    <td className="px-4 py-3 text-right text-gray-700">INR {rate.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* totals */}
                    <div className="mt-5 flex justify-end">
                        <div className="w-full sm:w-72 text-xs space-y-2">
                            <div className="flex justify-between text-gray-600">
                                <span>Total Amount</span>
                                <span className="text-gray-900 font-semibold">INR {total.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-gray-600">
                                <span>Paid Amount</span>
                                <span className="text-gray-900 font-semibold">INR {paid.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between border-t border-orange-200 pt-2 font-bold text-[#0b1b3d]">
                                <span>Balance Due</span>
                                <span className="text-orange-600">INR {balanceDue.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>

                    {invoice.comments && (
                        <div className="mt-6">
                            <div className="flex items-center gap-2 text-[#0b1b3d]">
                                <MessageSquare size={14} className="text-orange-500" />
                                <span className="text-sm font-bold">Comments</span>
                            </div>
                            <p className="mt-1.5 text-xs text-gray-600">{invoice.comments}</p>
                        </div>
                    )}

                    {/* thank-you strip */}
                    <div className="mt-8 rounded-xl border border-gray-200 bg-gray-50/70 p-5">
                        <div className="flex items-start gap-3">
                            <span className="text-orange-500 mt-0.5 shrink-0">
                                <ThumbsUp size={16} />
                            </span>
                            <div>
                                <p className="text-sm font-bold text-[#0b1b3d]">Thank you for your business!</p>
                                <p className="mt-1 text-xs text-gray-500">We appreciate your trust in {companyName}.</p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex items-start justify-between gap-3 text-[10px] text-gray-400 border-t border-gray-100 pt-3">
                        <div className="flex items-start gap-1.5">
                            <Globe size={12} className="mt-0.5 shrink-0" />
                            <div>
                                <p>Generated by {companyName}</p>
                                <p>This is a system generated invoice.</p>
                            </div>
                        </div>
                        <span>1/1</span>
                    </div>
                </div>

                {/* bottom bar */}
                <div className="bg-[#0b1b3d] px-8 sm:px-10 py-3 flex items-center gap-2 text-[10px] text-white/70">
                    <Globe size={12} />
                    <span>{footerUrl}</span>
                </div>
            </div>
        </div>
    );
};

/* ==========================================================
   "View" modal for Bills — same printable white-document look
   as ViewInvoiceModal, themed as a BILL (vendor instead of
   buyer/seller, expense head instead of line-item narration).
   #bill-print-area is the print target (see print rules below).
========================================================== */
const ViewBillModal = ({ bill, onClose }) => {
    const companyName = useMemo(readStoredCompanyName, []);
    const now = useMemo(() => new Date(), []);
    const dateLine = useMemo(
        () => now.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
        [now]
    );
    const timeLine = useMemo(
        () =>
            `${now.toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false
            })}`,
        [now]
    );
    const companyWebsite = useMemo(readStoredCompanyWebsite, []);
    const initials = useMemo(() => companyInitials(companyName), [companyName]);
    const amount = Number(bill.billAmount) || 0;
    const paid = Number(bill.paidAmount) || 0;
    const balanceDue = Math.max(amount - paid, 0);
    const currency = bill.amountType || "INR";
    const footerUrl = companyWebsite || companyName;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-4 sm:p-8 print:bg-white print:p-0 print:block">
            {/* ---- chrome: hidden entirely when printing ---- */}
            <div className="w-full max-w-2xl print:hidden mb-4 flex items-center justify-between sticky top-0">
                <span className="inline-flex items-center gap-2 text-xs font-medium text-gray-300 bg-black/40 border border-white/10 rounded-full px-3 py-1.5">
                    <Eye size={12} className="text-orange-400" /> Bill preview
                </span>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => window.print()}
                        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white text-xs font-semibold px-3.5 py-1.5 shadow-[0_6px_18px_-6px_rgba(255,107,0,0.55)] transition"
                    >
                        <Printer size={13} /> Print / Save as PDF
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-7 w-7 rounded-full flex items-center justify-center text-gray-300 bg-black/40 border border-white/10 hover:text-white hover:bg-white/10 transition"
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>

            {/* ---- the actual white, printable bill ---- */}
            <div
                id="bill-print-area"
                className="w-full max-w-2xl bg-white text-gray-900 rounded-2xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none print:max-w-none"
            >
                {/* top orange -> navy accent strip */}
                <div className="h-2 w-full bg-gradient-to-r from-orange-500 via-orange-500 to-[#0b1b3d]" />

                <div className="p-8 sm:p-10">
                    {/* header: timestamp | platform brand | company logo+name */}
                    <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1.5 text-[11px] text-gray-500">
                            <div className="flex items-center gap-1.5">
                                <Calendar size={13} className="text-gray-400" /> {dateLine}
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Clock size={13} className="text-gray-400" /> {timeLine}
                            </div>
                        </div>

                        <div className="hidden sm:flex flex-col items-center text-center">
                            <Building2 size={22} className="text-orange-500" />
                            <span className="mt-1 text-sm font-bold text-[#0b1b3d]">{PLATFORM_NAME}</span>
                        </div>

                        <div className="flex flex-col items-end text-right">
                            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-orange-500 to-[#0b1b3d] flex items-center justify-center text-white text-xs font-bold shrink-0">
                                {initials}
                            </div>
                            <span className="mt-1.5 text-sm font-semibold text-orange-600">{companyName}</span>
                            <span className="mt-1 h-px w-16 bg-orange-500/40" />
                        </div>
                    </div>

                    <h1 className="mt-8 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0b1b3d]">BILL</h1>
                    <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                        <span>
                            Bill #: <span className="text-orange-600 font-bold">BILL-{bill.id ?? "N/A"}</span>
                        </span>
                        <span className="text-gray-300">|</span>
                        <span>
                            Date: <span className="text-gray-900 font-medium">{bill.billDate || "N/A"}</span>
                        </span>
                    </div>
                    <div className="mt-4 h-px w-full bg-gradient-to-r from-orange-500/50 via-orange-300/30 to-transparent" />

                    {/* billed from (vendor) / billed to (this company) */}
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="border border-gray-200 rounded-xl p-5">
                            <div className="flex items-center gap-2.5">
                                <span className="h-7 w-7 shrink-0 rounded-full bg-orange-500 flex items-center justify-center text-white">
                                    <Users size={13} />
                                </span>
                                <h2 className="text-xs font-bold text-[#0b1b3d] tracking-wide">BILLED FROM</h2>
                            </div>
                            <p className="mt-3 text-sm font-semibold text-orange-600">{bill.vendorName || "N/A"}</p>
                            <p className="mt-1 text-xs text-gray-500">Expense head: {bill.expenseHead || "N/A"}</p>
                        </div>
                        <div className="border border-gray-200 rounded-xl p-5">
                            <div className="flex items-center gap-2.5">
                                <span className="h-7 w-7 shrink-0 rounded-full bg-orange-500 flex items-center justify-center text-white">
                                    <Building2 size={13} />
                                </span>
                                <h2 className="text-xs font-bold text-[#0b1b3d] tracking-wide">BILLED TO</h2>
                            </div>
                            <p className="mt-3 text-sm font-semibold text-gray-800">{companyName}</p>
                            <p className="mt-1 text-xs text-gray-500">Payment type: {bill.paymentType || "N/A"}</p>
                        </div>
                    </div>

                    {/* line item */}
                    <div className="mt-6 overflow-hidden rounded-xl">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-[#0b1b3d] text-[10px] font-bold text-white uppercase tracking-wider">
                                    <th className="text-left px-4 py-3">ID</th>
                                    <th className="text-left px-4 py-3">Description</th>
                                    <th className="text-right px-4 py-3">Qty</th>
                                    <th className="text-right px-4 py-3">Rate</th>
                                    <th className="text-right px-4 py-3">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b border-gray-100">
                                    <td className="px-4 py-3 text-gray-700">1</td>
                                    <td className="px-4 py-3 text-gray-700">{bill.narration || bill.expenseHead || "Bill Expense"}</td>
                                    <td className="px-4 py-3 text-right text-gray-700">1</td>
                                    <td className="px-4 py-3 text-right text-gray-700">{currency} {amount.toFixed(2)}</td>
                                    <td className="px-4 py-3 text-right text-gray-700">{currency} {amount.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* payment terms + notes (left) | totals box (right) */}
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="space-y-5">
                            <div className="flex items-start gap-3">
                                <span className="h-7 w-7 shrink-0 rounded-full border border-orange-200 bg-orange-50 flex items-center justify-center text-orange-500">
                                    <CreditCard size={13} />
                                </span>
                                <div>
                                    <h3 className="text-xs font-bold text-[#0b1b3d] tracking-wide">PAYMENT TERMS</h3>
                                    <p className="mt-1 text-xs text-gray-500">Paid via {bill.paymentType || "N/A"}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <span className="h-7 w-7 shrink-0 rounded-full border border-orange-200 bg-orange-50 flex items-center justify-center text-orange-500">
                                    <MessageSquare size={13} />
                                </span>
                                <div>
                                    <h3 className="text-xs font-bold text-[#0b1b3d] tracking-wide">NOTES</h3>
                                    <p className="mt-1 text-xs text-gray-500">{bill.comments || "No additional notes."}</p>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-xl border border-orange-100 bg-orange-50/40 p-5 text-xs space-y-3 h-fit">
                            <div className="flex justify-between pb-3 border-b border-dashed border-orange-200 text-gray-600">
                                <span>Bill Amount</span>
                                <span className="text-gray-900 font-semibold">{currency} {amount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between pb-3 border-b border-dashed border-orange-200 text-gray-600">
                                <span>Paid Amount</span>
                                <span className="text-gray-900 font-semibold">{currency} {paid.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center pt-1">
                                <span className="text-sm font-bold text-[#0b1b3d]">BALANCE DUE</span>
                                <span className="text-lg font-extrabold text-orange-600">{currency} {balanceDue.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>

                    {/* script-style thank you line */}
                    <div className="mt-9 flex items-center gap-4">
                        <span className="h-px flex-1 bg-gray-200" />
                        <span className="text-sm italic text-orange-600" style={{ fontFamily: "'Brush Script MT', cursive" }}>
                            Thank you for your business!
                        </span>
                        <span className="h-px flex-1 bg-gray-200" />
                    </div>

                    <div className="mt-6 flex items-start justify-between gap-3 text-[10px] text-gray-400 border-t border-gray-100 pt-3">
                        <div className="flex items-start gap-1.5">
                            <Globe size={12} className="mt-0.5 shrink-0" />
                            <div>
                                <p>Generated by {companyName}</p>
                                <p>This is a system generated bill.</p>
                            </div>
                        </div>
                        <span>1/1</span>
                    </div>
                </div>

                {/* bottom bar */}
                <div className="bg-[#0b1b3d] px-8 sm:px-10 py-3 flex items-center gap-2 text-[10px] text-white/70">
                    <Globe size={12} />
                    <span>{footerUrl}</span>
                </div>
            </div>
        </div>
    );
};

/* ==========================================================
   Revenue Trends modal — real line chart (month-over-month
   invoice revenue), plain SVG so no extra charting dependency
   is needed. Same dark card styling as every other modal in
   this dashboard — NOT the white card look.
========================================================== */
const monthKeyOf = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabelOf = (key) => {
    const [y, m] = key.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

const RevenueTrendsModal = ({ invoices, onClose }) => {
    const points = useMemo(() => {
        const byMonth = {};
        invoices.forEach((inv) => {
            const key = monthKeyOf(inv.invoiceDate);
            if (!key) return;
            byMonth[key] = (byMonth[key] || 0) + (Number(inv.totalAmount) || 0);
        });
        return Object.keys(byMonth)
            .sort()
            .map((key) => ({ key, label: monthLabelOf(key), total: byMonth[key] }));
    }, [invoices]);

    const width = 560;
    const height = 260;
    const padL = 56;
    const padR = 20;
    const padT = 16;
    const padB = 34;
    const innerW = width - padL - padR;
    const innerH = height - padT - padB;
    const maxVal = Math.max(1, ...points.map((p) => p.total));
    const stepX = points.length > 1 ? innerW / (points.length - 1) : 0;
    const coords = points.map((p, i) => ({
        ...p,
        x: padL + (points.length > 1 ? i * stepX : innerW / 2),
        y: padT + innerH - (p.total / maxVal) * innerH,
    }));
    const linePath = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(" ");
    const gridLines = 4;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0b0b0f] shadow-[0_32px_80px_-20px_rgba(0,0,0,0.8)]">
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                    <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                            <TrendingUp size={15} className="text-orange-500" />
                        </div>
                        <h3 className="text-white text-sm font-semibold">Revenue Trends</h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5">
                    {points.length === 0 ? (
                        <p className="text-sm text-gray-500 py-10 text-center">Add an invoice to see revenue trends.</p>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 mb-3">
                                <span className="h-2.5 w-4 rounded-sm bg-gradient-to-r from-orange-500 to-orange-400" />
                                <span className="text-xs text-gray-400">Invoice Revenue</span>
                            </div>

                            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
                                <defs>
                                    <linearGradient id="revenueLineGradient" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#f97316" />
                                        <stop offset="100%" stopColor="#fb923c" />
                                    </linearGradient>
                                </defs>

                                {Array.from({ length: gridLines + 1 }).map((_, i) => {
                                    const y = padT + (innerH / gridLines) * i;
                                    const val = Math.round(maxVal - (maxVal / gridLines) * i);
                                    return (
                                        <g key={i}>
                                            <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                                            <text x={padL - 10} y={y + 3.5} textAnchor="end" fontSize="10" fill="#6b7280">
                                                {val.toLocaleString("en-IN")}
                                            </text>
                                        </g>
                                    );
                                })}

                                <path d={linePath} fill="none" stroke="url(#revenueLineGradient)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

                                {coords.map((c, i) => (
                                    <g key={c.key}>
                                        <circle cx={c.x} cy={c.y} r="3.5" fill="#0b0b0f" stroke="#f97316" strokeWidth="2" />
                                        <text x={c.x} y={height - padB + 18} textAnchor="middle" fontSize="10" fill="#6b7280">
                                            {c.label}
                                        </text>
                                        <title>{`${c.label}: INR ${c.total.toLocaleString("en-IN")}`}</title>
                                    </g>
                                ))}
                            </svg>
                            <p className="text-center text-[11px] text-gray-500 mt-1">Month</p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ==========================================================
   Invoices table
========================================================== */
const columns = [
    "ID",
    "Invoice No",
    "Invoice Date",
    "Seller Details",
    "Buyer Details",
    "Invoice Amount",
    "Total Amount",
    "Payment Type",
    "Narration",
    "Comments",
    "Actions"
];

const InvoicesWorkspace = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [saving, setSaving] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [viewInvoice, setViewInvoice] = useState(null);
    const [showTrends, setShowTrends] = useState(false);

    // Load this company's invoices from the database on mount — the
    // backend scopes /invoices to whichever company the auth token
    // belongs to, so this never shows another company's data.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setLoadError("");
            try {
                const res = await fetch(INVOICES_ENDPOINT, { headers: authHeaders() });
                if (!res.ok) throw new Error(`load failed (${res.status})`);
                const data = await res.json();
                if (!cancelled) setInvoices((data?.data || []).map(fromApiInvoice));
            } catch (err) {
                if (!cancelled) {
                    // Backend/DB not reachable yet — fall back to the seed
                    // row locally so the screen still shows something,
                    // rather than a bare error.
                    setInvoices(SEED_INVOICES);
                    setLoadError("Couldn't reach the server — showing sample data. Add Invoice will retry saving to the database.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleSave = async (form) => {
        setSaving(true);
        try {
            const res = await fetch(INVOICES_ENDPOINT, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify(toApiInvoice(form))
            });
            if (!res.ok) throw new Error(`save failed (${res.status})`);
            const data = await res.json();
            const saved = (data?.data || [])[0];
            if (saved) {
                setInvoices((prev) => [fromApiInvoice(saved), ...prev]);
                setShowAddModal(false);
            } else {
                throw new Error("no invoice returned");
            }
        } catch (err) {
            alert("Couldn't save this invoice to the database. Please check your connection and try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        const prev = invoices;
        setInvoices((cur) => cur.filter((i) => i.id !== id)); // optimistic
        try {
            const res = await fetch(`${INVOICES_ENDPOINT}/${id}`, {
                method: "DELETE",
                headers: authHeaders()
            });
            if (!res.ok) throw new Error(`delete failed (${res.status})`);
        } catch (err) {
            setInvoices(prev); // roll back on failure
            alert("Couldn't delete this invoice from the database. Please try again.");
        }
    };

    const exportCSV = (label) => {
        const header = columns.slice(0, -1).join(",");
        const body = invoices
            .map((i) =>
                [i.id, i.invoiceNumber, i.invoiceDate, i.sellerAddress, i.buyerAddress, i.invoiceAmount, i.totalAmount, i.paymentType, i.narration, i.comments]
                    .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
                    .join(",")
            )
            .join("\n");
        const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoices-${label.toLowerCase()}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex flex-wrap items-center gap-3 mt-4 shrink-0">
                <ToolbarButton icon={Plus} label="Add Invoice" primary onClick={() => setShowAddModal(true)} disabled={saving} />
                <ToolbarButton icon={TrendingUp} label="Revenue Trends" onClick={() => setShowTrends(true)} />
                <ToolbarButton icon={FileSpreadsheet} label="Export All to Tally" onClick={() => exportCSV("Tally")} />
                <ToolbarButton icon={Download} label="Export All to QuickBooks" onClick={() => exportCSV("QuickBooks")} />
            </div>

            <div className="flex-1 min-h-0 mt-5 rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden flex flex-col">
                <div className="flex-1 min-h-0 overflow-auto thin-scrollbar">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 z-[1]">
                            <tr className="bg-[#0d1117] border-b border-white/10">
                                {columns.map((col) => (
                                    <th
                                        key={col}
                                        className="text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider px-4 py-3 whitespace-nowrap"
                                    >
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={columns.length} className="px-4 py-10 text-center text-gray-500 text-sm">
                                        <Loader2 size={14} className="inline animate-spin mr-2" /> Loading invoices…
                                    </td>
                                </tr>
                            )}
                            {!loading && loadError && (
                                <tr>
                                    <td colSpan={columns.length} className="px-4 py-3 text-center text-amber-400 text-xs bg-amber-500/5">
                                        {loadError}
                                    </td>
                                </tr>
                            )}
                            {!loading && invoices.length === 0 && (
                                <tr>
                                    <td colSpan={columns.length} className="px-4 py-10 text-center text-gray-500 text-sm">
                                        No invoices yet — click "Add Invoice" to create one.
                                    </td>
                                </tr>
                            )}
                            {!loading && invoices.map((inv) => (
                                <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                                    <td className="px-4 py-3.5 text-gray-400">{inv.id}</td>
                                    <td className="px-4 py-3.5 text-orange-300 font-medium whitespace-nowrap">{inv.invoiceNumber}</td>
                                    <td className="px-4 py-3.5 text-gray-300 whitespace-nowrap">{inv.invoiceDate}</td>
                                    <td className="px-4 py-3.5 text-gray-400 max-w-[180px] break-words">{inv.sellerAddress}</td>
                                    <td className="px-4 py-3.5 text-gray-400 max-w-[180px] break-words">{inv.buyerAddress}</td>
                                    <td className="px-4 py-3.5 text-gray-200 whitespace-nowrap">INR {inv.invoiceAmount}</td>
                                    <td className="px-4 py-3.5 text-gray-200 whitespace-nowrap">INR {inv.totalAmount}</td>
                                    <td className="px-4 py-3.5 text-gray-400 whitespace-nowrap">{inv.paymentType}</td>
                                    <td className="px-4 py-3.5 text-gray-400 max-w-[160px] break-words">{inv.narration}</td>
                                    <td className="px-4 py-3.5 text-gray-400 max-w-[160px] break-words">{inv.comments}</td>
                                    <td className="px-4 py-3.5">
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setViewInvoice(inv)}
                                                className="inline-flex items-center gap-1 rounded-full border border-blue-400/30 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 text-xs font-medium px-3 py-1.5 transition"
                                            >
                                                <Eye size={12} /> View
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(inv.id)}
                                                className="inline-flex items-center gap-1 rounded-full border border-red-400/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-medium px-3 py-1.5 transition"
                                            >
                                                <Trash2 size={12} /> Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showAddModal && <AddInvoiceModal onClose={() => setShowAddModal(false)} onSave={handleSave} />}
            {viewInvoice && <ViewInvoiceModal invoice={viewInvoice} onClose={() => setViewInvoice(null)} />}
            {showTrends && <RevenueTrendsModal invoices={invoices} onClose={() => setShowTrends(false)} />}
        </div>
    );
};

/* ==========================================================
   "Show Dashboard" modal for Bills — expense breakdown by
   Expense Head, plain SVG bar chart, same dark card styling
   as RevenueTrendsModal (Bills' counterpart of that view).
========================================================== */
const BillDashboardModal = ({ bills, onClose }) => {
    const bars = useMemo(() => {
        const byHead = {};
        bills.forEach((b) => {
            const key = b.expenseHead || "Other";
            byHead[key] = (byHead[key] || 0) + (Number(b.billAmount) || 0);
        });
        return Object.keys(byHead)
            .map((key) => ({ key, total: byHead[key] }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 8);
    }, [bills]);

    const totalSpend = useMemo(() => bills.reduce((sum, b) => sum + (Number(b.billAmount) || 0), 0), [bills]);
    const totalPaid = useMemo(() => bills.reduce((sum, b) => sum + (Number(b.paidAmount) || 0), 0), [bills]);
    const maxVal = Math.max(1, ...bars.map((b) => b.total));

    const width = 560;
    const barHeight = 26;
    const gap = 14;
    const padL = 110;
    const padR = 60;
    const height = bars.length ? bars.length * (barHeight + gap) + gap : 80;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0b0b0f] shadow-[0_32px_80px_-20px_rgba(0,0,0,0.8)]">
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                    <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                            <TrendingUp size={15} className="text-orange-500" />
                        </div>
                        <h3 className="text-white text-sm font-semibold">Bills Dashboard</h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5">
                    <div className="grid grid-cols-2 gap-3 mb-5">
                        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Total Billed</p>
                            <p className="mt-1 text-lg font-bold text-white">INR {totalSpend.toLocaleString("en-IN")}</p>
                        </div>
                        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Total Paid</p>
                            <p className="mt-1 text-lg font-bold text-orange-400">INR {totalPaid.toLocaleString("en-IN")}</p>
                        </div>
                    </div>

                    {bars.length === 0 ? (
                        <p className="text-sm text-gray-500 py-10 text-center">Add a bill to see the expense breakdown.</p>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 mb-3">
                                <span className="h-2.5 w-4 rounded-sm bg-gradient-to-r from-orange-500 to-orange-400" />
                                <span className="text-xs text-gray-400">Spend by Expense Head</span>
                            </div>
                            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
                                <defs>
                                    <linearGradient id="expenseBarGradient" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#f97316" />
                                        <stop offset="100%" stopColor="#fb923c" />
                                    </linearGradient>
                                </defs>
                                {bars.map((b, i) => {
                                    const y = gap + i * (barHeight + gap);
                                    const innerW = width - padL - padR;
                                    const barW = (b.total / maxVal) * innerW;
                                    return (
                                        <g key={b.key}>
                                            <text x={padL - 10} y={y + barHeight / 2 + 4} textAnchor="end" fontSize="11" fill="#d1d5db">
                                                {b.key.length > 16 ? `${b.key.slice(0, 15)}…` : b.key}
                                            </text>
                                            <rect x={padL} y={y} width={innerW} height={barHeight} rx="6" fill="rgba(255,255,255,0.04)" />
                                            <rect x={padL} y={y} width={Math.max(barW, 2)} height={barHeight} rx="6" fill="url(#expenseBarGradient)" />
                                            <text x={padL + innerW + 8} y={y + barHeight / 2 + 4} fontSize="10.5" fill="#9ca3af">
                                                {b.total.toLocaleString("en-IN")}
                                            </text>
                                            <title>{`${b.key}: INR ${b.total.toLocaleString("en-IN")}`}</title>
                                        </g>
                                    );
                                })}
                            </svg>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ==========================================================
   Bills table
========================================================== */
const billColumns = [
    "ID",
    "Vendor Name",
    "Bill Amount",
    "Expense Head",
    "Bill Date",
    "Payment Type",
    "Paid Amount",
    "Amount Type",
    "Narration",
    "Comments",
    "Actions"
];

const BillsWorkspace = () => {
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [saving, setSaving] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [viewBill, setViewBill] = useState(null);
    const [showDashboard, setShowDashboard] = useState(false);

    // Load this company's bills from the database on mount — the
    // backend scopes /bills to whichever company the auth token
    // belongs to, so this never shows another company's data.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setLoadError("");
            try {
                const res = await fetch(BILLS_ENDPOINT, { headers: authHeaders() });
                if (!res.ok) throw new Error(`load failed (${res.status})`);
                const data = await res.json();
                if (!cancelled) setBills((data?.data || []).map(fromApiBill));
            } catch (err) {
                if (!cancelled) {
                    // Backend/DB not reachable yet — fall back to the seed
                    // row locally so the screen still shows something,
                    // rather than a bare error.
                    setBills(SEED_BILLS);
                    setLoadError("Couldn't reach the server — showing sample data. Add Bill will retry saving to the database.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleSave = async (form) => {
        setSaving(true);
        try {
            const res = await fetch(BILLS_ENDPOINT, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify(toApiBill(form))
            });
            if (!res.ok) throw new Error(`save failed (${res.status})`);
            const data = await res.json();
            const saved = (data?.data || [])[0];
            if (saved) {
                setBills((prev) => [fromApiBill(saved), ...prev]);
                setShowAddModal(false);
            } else {
                throw new Error("no bill returned");
            }
        } catch (err) {
            alert("Couldn't save this bill to the database. Please check your connection and try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        const prev = bills;
        setBills((cur) => cur.filter((b) => b.id !== id)); // optimistic
        try {
            const res = await fetch(`${BILLS_ENDPOINT}/${id}`, {
                method: "DELETE",
                headers: authHeaders()
            });
            if (!res.ok) throw new Error(`delete failed (${res.status})`);
        } catch (err) {
            setBills(prev); // roll back on failure
            alert("Couldn't delete this bill from the database. Please try again.");
        }
    };

    const exportCSV = (label) => {
        const header = billColumns.slice(0, -1).join(",");
        const body = bills
            .map((b) =>
                [b.id, b.vendorName, b.billAmount, b.expenseHead, b.billDate, b.paymentType, b.paidAmount, b.amountType, b.narration, b.comments]
                    .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
                    .join(",")
            )
            .join("\n");
        const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `bills-${label.toLowerCase()}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex flex-wrap items-center gap-3 mt-4 shrink-0">
                <ToolbarButton icon={Plus} label="Add Bill" primary onClick={() => setShowAddModal(true)} disabled={saving} />
                <ToolbarButton icon={TrendingUp} label="Show Dashboard" onClick={() => setShowDashboard(true)} />
                <ToolbarButton icon={FileSpreadsheet} label="Export All to Tally" onClick={() => exportCSV("Tally")} />
                <ToolbarButton icon={Download} label="Export All to QuickBooks" onClick={() => exportCSV("QuickBooks")} />
            </div>

            <div className="flex-1 min-h-0 mt-5 rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden flex flex-col">
                <div className="flex-1 min-h-0 overflow-auto thin-scrollbar">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 z-[1]">
                            <tr className="bg-[#0d1117] border-b border-white/10">
                                {billColumns.map((col) => (
                                    <th
                                        key={col}
                                        className="text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider px-4 py-3 whitespace-nowrap"
                                    >
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={billColumns.length} className="px-4 py-10 text-center text-gray-500 text-sm">
                                        <Loader2 size={14} className="inline animate-spin mr-2" /> Loading bills…
                                    </td>
                                </tr>
                            )}
                            {!loading && loadError && (
                                <tr>
                                    <td colSpan={billColumns.length} className="px-4 py-3 text-center text-amber-400 text-xs bg-amber-500/5">
                                        {loadError}
                                    </td>
                                </tr>
                            )}
                            {!loading && bills.length === 0 && (
                                <tr>
                                    <td colSpan={billColumns.length} className="px-4 py-10 text-center text-gray-500 text-sm">
                                        No bills yet — click "Add Bill" to create one.
                                    </td>
                                </tr>
                            )}
                            {!loading && bills.map((b) => (
                                <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                                    <td className="px-4 py-3.5 text-gray-400">{b.id}</td>
                                    <td className="px-4 py-3.5 text-orange-300 font-medium whitespace-nowrap">{b.vendorName}</td>
                                    <td className="px-4 py-3.5 text-gray-200 whitespace-nowrap">{b.amountType || "INR"} {b.billAmount}</td>
                                    <td className="px-4 py-3.5 text-gray-400 whitespace-nowrap">{b.expenseHead}</td>
                                    <td className="px-4 py-3.5 text-gray-300 whitespace-nowrap">{b.billDate}</td>
                                    <td className="px-4 py-3.5 text-gray-400 whitespace-nowrap">{b.paymentType}</td>
                                    <td className="px-4 py-3.5 text-gray-200 whitespace-nowrap">{b.amountType || "INR"} {b.paidAmount}</td>
                                    <td className="px-4 py-3.5 text-gray-400 whitespace-nowrap">{b.amountType}</td>
                                    <td className="px-4 py-3.5 text-gray-400 max-w-[160px] break-words">{b.narration}</td>
                                    <td className="px-4 py-3.5 text-gray-400 max-w-[160px] break-words">{b.comments}</td>
                                    <td className="px-4 py-3.5">
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setViewBill(b)}
                                                className="inline-flex items-center gap-1 rounded-full border border-blue-400/30 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 text-xs font-medium px-3 py-1.5 transition"
                                            >
                                                <Eye size={12} /> View
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(b.id)}
                                                className="inline-flex items-center gap-1 rounded-full border border-red-400/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-medium px-3 py-1.5 transition"
                                            >
                                                <Trash2 size={12} /> Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showAddModal && <AddBillModal onClose={() => setShowAddModal(false)} onSave={handleSave} />}
            {viewBill && <ViewBillModal bill={viewBill} onClose={() => setViewBill(null)} />}
            {showDashboard && <BillDashboardModal bills={bills} onClose={() => setShowDashboard(false)} />}
        </div>
    );
};

const CompanyAccounting = () => {
    const navigate = useNavigate();
    const [activeView, setActiveView] = useState(null);

    return (
        <>
            <style>{`
                .thin-scrollbar { scrollbar-width: thin; scrollbar-color: transparent transparent; }
                .thin-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
                .thin-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .thin-scrollbar::-webkit-scrollbar-thumb { background-color: transparent; border-radius: 9999px; transition: background-color 0.2s ease; }
                .thin-scrollbar:hover { scrollbar-color: rgba(249, 115, 22, 0.35) transparent; }
                .thin-scrollbar:hover::-webkit-scrollbar-thumb { background-color: rgba(249, 115, 22, 0.35); }
                .thin-scrollbar:hover::-webkit-scrollbar-thumb:hover { background-color: rgba(249, 115, 22, 0.55); }
                @media print {
                    body * { visibility: hidden; }
                    #invoice-print-area, #invoice-print-area *,
                    #bill-print-area, #bill-print-area * { visibility: visible; }
                    #invoice-print-area, #bill-print-area { position: absolute; inset: 0; width: 100%; margin: 0; }
                }
            `}</style>
            <div id="sec-company_accounting" className="h-full flex flex-col py-6 overflow-hidden pr-1">
                {activeView === null && (
                    <button
                        type="button"
                        onClick={() => navigate("/app/dashboard-preview")}
                        className="shrink-0 self-start mb-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-white/5 border border-white/10 text-gray-300 hover:text-orange-400 hover:border-orange-600/40 hover:bg-orange-600/10 transition"
                    >
                        <ArrowLeft size={15} /> Back
                    </button>
                )}
                <div className="shrink-0">
                    <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
                        <Receipt className="text-orange-500" size={26} />
                        Accounting
                    </h1>
                    <p className="text-gray-400 mt-1.5 text-sm">
                        Manage invoices and bills for your business.
                    </p>
                </div>

                {activeView === null && (
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl shrink-0">
                        <AccountingCard
                            icon={FileText}
                            eyebrow="Billing"
                            title="Invoices"
                            description="Create, send, and track all your customer invoices in one place."
                            onOpen={() => setActiveView("invoices")}
                        />
                        <AccountingCard
                            icon={CreditCard}
                            eyebrow="Payables"
                            title="Bills"
                            description="Record and manage vendor bills and payment records."
                            onOpen={() => setActiveView("bills")}
                        />
                    </div>
                )}

                {activeView === "invoices" && (
                    <div className="flex-1 min-h-0 flex flex-col">
                        <button
                            type="button"
                            onClick={() => setActiveView(null)}
                            className="mt-6 shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-orange-400 transition w-fit"
                        >
                            <ArrowLeft size={14} /> Back
                        </button>
                        <div className="flex items-center gap-2 mt-4 shrink-0">
                            <FileText size={16} className="text-orange-500" />
                            <h2 className="text-sm font-semibold text-white">Invoices</h2>
                        </div>
                        <InvoicesWorkspace />
                    </div>
                )}

                {activeView === "bills" && (
                    <div className="flex-1 min-h-0 flex flex-col">
                        <button
                            type="button"
                            onClick={() => setActiveView(null)}
                            className="mt-6 shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-orange-400 transition w-fit"
                        >
                            <ArrowLeft size={14} /> Back
                        </button>
                        <div className="flex items-center gap-2 mt-4 shrink-0">
                            <CreditCard size={16} className="text-orange-500" />
                            <h2 className="text-sm font-semibold text-white">Bills</h2>
                        </div>
                        <BillsWorkspace />
                    </div>
                )}
            </div>
        </>
    );
};

export default CompanyAccounting;